import { useMemo } from 'react';
import { useInsights, type Loaded } from '../pipeline/useInsights';

/** The status page, for the addresses its answer does not carry. */
export const STATUS_PAGE = 'https://status.revenexx.com';

/** Where a service owner reads how to have their service probed. */
export const PROBE_HOW_TO = '/docs/default/component/backstage/status-probes/';

export type StatusKey =
  'operational' | 'degraded' | 'partial' | 'major' | 'maintenance' | 'no_data';

export type StatusIncident = {
  id: string;
  title: string;
  severity: string;
  state: string;
  startedAt: string;
  resolvedAt: string | null;
  public: boolean;
  /** The public incident page; null for an internal incident. */
  url: string | null;
  /** The incident in the status admin, for every incident. */
  adminUrl: string;
};

/** What the status service says about one service (docs/status-probes.md). */
export type ServiceStatus = {
  slug: string;
  /** The repository it was synced from, `org/repo`; null when made by hand. */
  repository: string | null;
  status: StatusKey;
  /** Whether its probes recorded anything in the window. */
  measured: boolean;
  windowDays: number;
  /** Null exactly when `measured` is false. */
  uptimePct: number | null;
  incidents: {
    inWindow: number;
    open: StatusIncident[];
  };
  /** The public service page; null for an internal service. */
  url: string | null;
};

/** An incident as the status service's read API writes it. */
export type WireStatusIncident = {
  id: string;
  title: string;
  severity: string;
  state: string;
  started_at: string;
  resolved_at: string | null;
  public: boolean;
  url: string | null;
  admin_url: string;
};

export const statusIncidentFromWire = (
  i: WireStatusIncident,
): StatusIncident => ({
  id: i.id,
  title: i.title,
  severity: i.severity,
  state: i.state,
  startedAt: i.started_at,
  resolvedAt: i.resolved_at,
  public: i.public,
  url: i.url,
  adminUrl: i.admin_url,
});

/** The answer as the status service writes it. */
type Wire = {
  slug: string;
  repository?: string | null;
  status: StatusKey;
  measured: boolean;
  window_days: number;
  uptime_pct: number | null;
  incidents: {
    in_window: number;
    open: WireStatusIncident[];
  };
  url: string | null;
};

function fromWire(wire: Wire): ServiceStatus {
  return {
    slug: wire.slug,
    repository: wire.repository ?? null,
    status: wire.status,
    measured: wire.measured,
    windowDays: wire.window_days,
    uptimePct: wire.measured ? wire.uptime_pct : null,
    incidents: {
      inWindow: wire.incidents.in_window,
      open: wire.incidents.open.map(statusIncidentFromWire),
    },
    url: wire.url,
  };
}

/**
 * The status service's own answer for a name it does not know. A 404 without
 * it — a route or a proxy endpoint that is not there — is a failure, not
 * "not measured".
 */
const isUnknownService = (body: unknown) =>
  (body as { error?: unknown } | undefined)?.error === 'not_found';

/**
 * What the status service knows about the component named `name`, asked
 * through the backend's `/status` proxy, which adds the token — the browser
 * never holds it. `data` is null when the status service does not know the
 * component: it answers that it knows no service by that name, or the
 * service it knows by that name was synced from another repository than
 * `repository`, the component's own.
 */
export function useServiceStatus(
  name: string,
  repository?: string,
): Loaded<ServiceStatus | null> {
  const answer = useInsights<Wire | null>(
    `/status/services/${encodeURIComponent(name)}`,
    { plugin: 'proxy', notFound: isUnknownService },
  );
  return useMemo((): Loaded<ServiceStatus | null> => {
    if (answer.data === undefined) {
      return { loading: answer.loading, error: answer.error };
    }
    if (answer.data === null) return { loading: false, data: null };
    const service = fromWire(answer.data);
    const elsewhere =
      !!repository &&
      !!service.repository &&
      service.repository.toLowerCase() !== repository.toLowerCase();
    return { loading: false, data: elsewhere ? null : service };
  }, [answer, repository]);
}

export const STATUS_LABEL: Record<StatusKey, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  partial: 'Partial outage',
  major: 'Major outage',
  maintenance: 'Under maintenance',
  no_data: 'No data',
};

/** The status page's own colours, so a state reads the same in both places. */
export const STATUS_COLOUR: Record<StatusKey, string> = {
  operational: '#22C55E',
  degraded: '#EAB308',
  partial: '#F97316',
  major: '#EF4444',
  maintenance: '#620DFF',
  no_data: '#9CA3AF',
};

/**
 * An uptime to two places, never rounded up: 99.996 % is 99.99 %, because
 * "100 %" claims no check ever failed.
 */
export function formatUptime(pct: number): string {
  if (pct >= 100) return '100 %';
  // The nudge only absorbs floating point — 99.99 * 100 is 9998.999… — and is
  // far below the thousandths the status service answers in.
  const hundredths = Math.floor(pct * 100 + 1e-6);
  return `${(hundredths / 100).toFixed(2)} %`;
}
